import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Dropdown, OverlayTrigger } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { AiOutlineSetting } from 'react-icons/ai';
import { BiCopy, BiTransferAlt, BiX } from 'react-icons/bi';
import { BsFileEarmarkPdfFill, BsFileEarmarkText, BsPlusLg, BsTrash3, BsUpload, BsXLg } from "react-icons/bs";
import { IoStop } from 'react-icons/io5';
import { MdSend } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PreferenceContext } from '../../App';
import API_ROUTES from '../../core/constants/api';
import { LS_AI_CONFIG_KEY, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_UI_ELEMENTS_CONFIG_KEY, ReportBasis, ReportPageType, ReportSection } from '../../core/constants/common';
import AICopilotConfigService from '../../core/service/AICopilotConfigService';
import GeneralDisclosureService from '../../core/service/GeneralDisclosureService';
import ReportAIService from '../../core/service/ReportAIService';
import ReportDataService from '../../core/service/ReportDataService';
import ReportService from '../../core/service/ReportService';
import ReportingUtils from '../../core/utilities/ReportingUtils';
import Utils from '../../core/utilities/Utils';
import esg_green from '../../images/esg_ai_Green.png';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import './AITextGenWidget.scss';

/**
 * Copilot expects quantitative_metrics[].values[] for generation. Some topics only
 * populate datapoints[].values[]; mirror those rows onto the metric-level values array.
 * @param {object} reportData - Payload report_data (must include year when available).
 */
export const enrichReportDataQuantitativeValuesFromDatapoints = (reportData) => {
	if (!reportData || typeof reportData !== 'object') return;

	const reportYear = reportData.year;

	const datapointRowHasQuantData = (v) => {
		if (!v || typeof v !== 'object') return false;
		const y = v.year;
		const hasYear = y !== undefined && y !== null && String(y).trim() !== '';
		const o = v.organisation_total;
		const hasOrg = o !== undefined && o !== null && String(o).trim() !== '';
		return hasYear && hasOrg;
	};

	const normalizeYearsToReportYearWhenSinglePeriod = (rows) => {
		if (!reportYear || !Array.isArray(rows) || rows.length === 0) return rows;
		const definedYears = rows
			.map(r => r.year)
			.filter(y => y !== undefined && y !== null && String(y).trim() !== '');
		const unique = [...new Set(definedYears.map(y => String(y)))];
		if (unique.length <= 1) {
			return rows.map(r => ({ ...r, year: reportYear }));
		}
		return rows;
	};

	const enrichMetric = (metric) => {
		if (!metric || typeof metric !== 'object') return;
		const existing = metric.values;
		if (Array.isArray(existing) && existing.length > 0) return;

		const dps = metric.datapoints;
		if (!Array.isArray(dps) || dps.length === 0) return;

		const collected = [];
		dps.forEach(dp => {
			(dp.values || []).forEach(v => {
				if (datapointRowHasQuantData(v)) {
					collected.push({ ...v });
				}
			});
		});

		if (collected.length === 0) return;

		metric.values = normalizeYearsToReportYearWhenSinglePeriod(collected);
	};

	const walkSupplementaryItem = (item) => {
		if (!item || typeof item !== 'object') return;

		if (Array.isArray(item.quantitative_metrics)) {
			item.quantitative_metrics.forEach(enrichMetric);
		}

		if (Array.isArray(item.sections)) {
			item.sections.forEach(sec => {
				if (sec && Array.isArray(sec.supplementary_data)) {
					sec.supplementary_data.forEach(walkSupplementaryItem);
				}
			});
		}
	};

	const sup = reportData.supplementary_data;
	if (!Array.isArray(sup)) return;
	sup.forEach(walkSupplementaryItem);
};

const isGhgLikeReportingStandard = (standard) => standard === 'GHG' || standard === 'UAE_FDL';

const AITextGenWithPrompt = ({
	t,
	type = 'REPORTING_STUDIO',
	page,
	section,
	periodYear,
	regulationId,
	regulationName,
	regulationFrequency,
	materialTopic,
	onHide,
	onTransferToEditor,
	jobId,
	brsrData,
	descriptionData,
	reportingStandard,
	language,
	report, // Added report prop
	copilotHistory, // Added copilot prop
	hideCopilotHistory,
	reportName,
	periodStart,
	periodEnd,
	entities
}) => {

	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
	const aiConfig = JSON.parse(localStorage.getItem(LS_AI_CONFIG_KEY) ?? null);
	const uiElementsConfig = JSON.parse(localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY)) ?? null;

	const [loading, setLoading] = useState(false);
	const [loadingPrompt, setLoadingPrompt] = useState('');
	const [text, setText] = useState([]);
	const [refinedText, setRefinedText] = useState({ rating: -1 });
	const [wordCount, setWordCount] = useState(aiConfig?.length ? aiConfig.length.type : 'AUTO');
	const [lastAPICallContext, setLastAPICallContext] = useState('');
	const [description, setDescription] = useState('');
	const location = useLocation();

	// Add AbortController for request cancellation
	const abortControllerRef = useRef(null);

	const companyName = lsData.profile.company.legalName;
	const companyId = lsData.profile.company.id;

	// Helper function to validate and provide fallback values for period parameters
	const getValidPeriodParams = () => {
		const validPeriodStart = periodStart && periodStart !== 'undefined' ? periodStart : `${periodYear}-01-01`;
		const validPeriodEnd = periodEnd && periodEnd !== 'undefined' ? periodEnd : `${periodYear}-12-31`;
		const validEntities = entities && Array.isArray(entities) && entities.length > 0 ? entities : [];
		
		return {
			periodStart: validPeriodStart,
			periodEnd: validPeriodEnd,
			entities: validEntities
		};
	};

	const textArea = useRef();
	const { preference } = useContext(PreferenceContext);
	const [audience, setAudience] = useState([]);
	const [narratedBy, setNarratedBy] = useState([]);
	const [activeAudience, setActiveAudience] = useState(aiConfig?.audience ? aiConfig.audience.type : 'AUDIENCE1');
	const [activeNarratedBy, setActiveNarratedBy] = useState(aiConfig?.narratedBy ? aiConfig.narratedBy.type : 'ROLE1');
	const [moreSettings, setMoreSettings] = useState(false);
	const [userInput, setUserInput] = useState('');
	const [fileUploader, setFileUploader] = useState('');
	const fileInputRef = useRef();
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const chatEndRef = useRef(null);
	const [showSettings, setShowSettings] = useState(false);
	const [showFiles, setShowFiles] = useState(false);
	const [showHistory, setShowHistory] = useState(false);

	// Chat history state variables
	const [chatHistory, setChatHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyStartLimit, setHistoryStartLimit] = useState(0);
	const [historyEndLimit, setHistoryEndLimit] = useState(5);
	const [hasMoreHistory, setHasMoreHistory] = useState(true);
	const historyScrollRef = useRef(null);
	const [showClearHistoryConfirmation, setShowClearHistoryConfirmation] = useState(false);
	
	// Lazy loading and read more/less state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [justFinishedLoadingMore, setJustFinishedLoadingMore] = useState(false);
	const [showReadMore, setShowReadMore] = useState(false);
	const [expandedItems, setExpandedItems] = useState(new Set());
	const [scrollPosition, setScrollPosition] = useState(0);
	const [hasChatHistory, setHasChatHistory] = useState(false);

	useEffect(() => {

		getFiles();
		fetchCopilotSettings();
		return () => {
			// Only clear text on actual unmount, not on re-renders
			// setText([]);
			// Cleanup: abort any ongoing request when component unmounts
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};

	}, []);

	useEffect(() => {
		setShowHistory(copilotHistory);
	}, [copilotHistory]);

	useEffect(() => {
		if (!loading) setLoadingPrompt('');
	}, [loading]);

	useEffect(() => {
		if (showHistory && chatHistory.length === 0) {
			// Reset pagination when opening history
			setHistoryStartLimit(0);
			setHistoryEndLimit(5);
			setHasMoreHistory(true);
			fetchChatHistory(false);
		}
		
		// Scroll to bottom when history panel is opened
		if (showHistory && historyScrollRef.current) {
			setTimeout(() => {
				historyScrollRef.current.scrollTop = historyScrollRef.current.scrollHeight;
			}, 100);
		}
	}, [showHistory]);

	// Get job ID from report data or props
	const getJobId = () => {
		// First try to get from report data
		if (report?.job?.id) {
			return report.job.id;
		}
		// Fallback to jobId prop
		if (jobId) {
			return jobId;
		}
		// If still no job ID, try to get from localStorage or other sources
		const reportData = JSON.parse(localStorage.getItem('currentReport') || '{}');
		if (reportData?.job?.id) {
			return reportData.job.id;
		}
		return null;
	};

	// Resolve report name for payloads
	const getReportName = () => {
		if (reportName) return reportName;
		else{
			return report.name || report.title || '';
		}
	};

	// Resolve report page code for payloads
	const getReportPageCode = () => {
		if (!page?.type) return page?.name || '';
		
		// Get section name and page name
		const sectionName = section?.name || '';
		const pageName = page?.name || '';
		
		// Environment pages
		if (page.type === ReportPageType.ENERGY || 
			page.type === ReportPageType.CARBON_EMISSION || 
			page.type === ReportPageType.WASTE || 
			page.type === ReportPageType.WATER || 
			page.type === ReportPageType.ENV_OVERVIEW ||
			sectionName.toLowerCase().includes('environment')) {
			return 'ENV_' + pageName.replace(/\s+/g, '_').toUpperCase();
		}
		
		// Social pages
		if (page.type === ReportPageType.CUSTOMERS || 
			page.type === ReportPageType.COMMUNITY || 
			page.type === ReportPageType.EMPLOYEES || 
			page.type === ReportPageType.SUPPLIERS || 
			page.type === ReportPageType.SOCIAL_OVERVIEW ||
			sectionName.toLowerCase().includes('social')) {
			return 'SOC_' + pageName.replace(/\s+/g, '_').toUpperCase();
		}
		
		// Governance pages
		if (page.type === ReportPageType.ESG_GOV || 
			page.type === ReportPageType.BOARD_GOV || 
			page.type === ReportPageType.HUMAN_RIGHTS || 
			page.type === ReportPageType.ETHICS_COMP || 
			page.type === ReportPageType.TAX_POLICY || 
			page.type === ReportPageType.BC_DIS_RECOVERY || 
			page.type === ReportPageType.GOV_OVERVIEW ||
			sectionName.toLowerCase().includes('governance')) {
			return 'GOV_' + pageName.replace(/\s+/g, '_').toUpperCase();
		}
		
		// Special cases
		if (page.type === ReportPageType.ABOUT_COMPANY) return 'ABOUT_COMPANY';
		if (page.type === ReportPageType.ABOUT_REPORT) return 'ABOUT_REPORT';
		if (page.type === ReportPageType.ESG_HIGHLIGHTS) return 'ESG_HIGHLIGHTS';
		if (page.type === ReportPageType.ESG_GOALS) return 'ESG_GOALS';
		
		// Others: Section name in CAPS + _ + page name in CAPS
		const formattedSectionName = sectionName.replace(/\s+/g, '_').toUpperCase();
		const formattedPageName = pageName.replace(/\s+/g, '_').toUpperCase();
		return formattedSectionName + '_' + formattedPageName;
	};

	const processWordLimit = (wordCount) => {
		if (wordCount) {
			let wCount = '';
			if (wordCount === 'SHORT') {
				wCount = '150-200';
			} else if (wordCount === 'MEDIUM') {
				wCount = '200-350';
			} else if (wordCount === 'LONG') {
				wCount = '350-500';
			} else if (wordCount === 'AUTO') {
				wCount = 'auto';
			}
			return wCount;
		} else {
			return 'auto';
		}
	}

	const generateText = () => {
		if (type === 'MATERIAL_TOPIC') {
			getRefinedTextForMaterialTopic();
			return;
		}
		if (type === 'BRSR_STUDIO') {
			getRefinedTextForBRSR();
			return;
		}
		if (type === 'CONTENT_BLOCK') {
			getRefinedTextForContentBlock();
			return;
		}
		if (!page?.type) return;

		// Check if this is GHG Insights page - MUST be checked BEFORE ESG_HIGHLIGHTS
		// Check by page type (INSIGHTS = 109) or by reporting standard and page/section name
		const isGHGInsightsPage = isGhgLikeReportingStandard(reportingStandard) && (
			page.type === ReportPageType.INSIGHTS || 
			page?.name?.toLowerCase().includes('insights') || 
			section?.name?.toLowerCase().includes('insights')
		);

		if (isGHGInsightsPage) {
			fetchGHGInsightsDataWithMetrics();
			return;
		}

		if (page.type === ReportPageType.ESG_HIGHLIGHTS) {
			fetchESGHighlightsDataWithMetrics();
			return;
		}

		// Check if this is an ESG Overview page
		if (isESGOverview(page.type)) {
			fetchESGOverviewAggregatedDataWithDataPoints();
			return;
		}
		generateConfigDependentText();
	};

	const generateConfigDependentText = (promptOverride) => {
		const effectiveInput = (promptOverride !== undefined && promptOverride !== null) ? promptOverride : userInput;
		if (effectiveInput == '' || effectiveInput == null || (typeof effectiveInput === 'string' && effectiveInput.trim() === '')) {
			toast.warning(t('PleaseProvidePrompt.label'), {
				position: toast.POSITION.TOP_RIGHT,
			});
			return;
		}
		if (promptOverride !== undefined && promptOverride !== null) {
			setUserInput(promptOverride);
		}
		setLoadingPrompt(effectiveInput);
		fetchStreamedDataWithDataPoints(page, promptOverride);
	};

	const fetchCopilotSettings = () => {
		AICopilotConfigService.getCopilotSettings().then(res => {
			if (res.status !== 200) return;
			const audienceItems = res.data.params.filter(item => item.paramType === 'AUDIENCE');
			const roleItems = res.data.params.filter(item => item.paramType === 'ROLE');

			const sortedAudienceItems = [...audienceItems].sort((a, b) => a.id - b.id);
			const sortedRoleItems = [...roleItems].sort((a, b) => a.id - b.id);

			setAudience(sortedAudienceItems);
			setNarratedBy(sortedRoleItems);
		}).catch(err => { });
	};

	const fetchChatHistory = useCallback(async (isLoadMore = false) => {
		
		// Update pagination BEFORE making the API call
		if (isLoadMore) {
			// For load more: update limits before API call
			const newStartLimit = historyStartLimit + 5;
			const newEndLimit = historyEndLimit + 5;
			setHistoryStartLimit(newStartLimit);
			setHistoryEndLimit(newEndLimit);
			setIsLoadingMore(true);
		} else {
			// For initial load: reset to 0-5
			setHistoryStartLimit(0);
			setHistoryEndLimit(5);
			setHistoryLoading(true);
		}
		
		// Use the current limits for the API call
		const currentStartLimit = isLoadMore ? historyStartLimit + 5 : 0;
		const currentEndLimit = isLoadMore ? historyEndLimit + 5 : 5;
		
		AICopilotConfigService.getCopoliotHistory(companyId, lsData?.profile?.user?.id, currentStartLimit, currentEndLimit, getReportName(), getReportPageCode()).then(response => {
			if (response.status === 200) {
				const data = response.data;
				if (data && Array.isArray(data)) {
					
					// Process conversations for display
					if (data.length > 0) {
						// Process conversations (reverse to show latest at bottom)
						const conversationText = [...data].reverse().map((conversation, index) => {
							
							// Try different data structure patterns for each conversation
							if (conversation.messages && Array.isArray(conversation.messages)) {
								return conversation.messages.map(msg => ({
									question: msg.question || msg.user_query || '',
									answer: msg.answer || msg.response || '',
									timestamp: msg.timestamp || msg.created_at || new Date().toISOString()
								}));
							} else if (conversation.user_query) {
								return {
									question: conversation.user_query,
									answer: conversation.response || conversation.answer || 'No response available',
									timestamp: conversation.created_at || conversation.timestamp || new Date().toISOString()
								};
							} else if (conversation.question && conversation.answer) {
								return {
									question: conversation.question,
									answer: conversation.answer,
									timestamp: conversation.timestamp || new Date().toISOString()
								};
							} else {
								return {
									question: conversation.user_query || conversation.question || 'Previous conversation',
									answer: conversation.response || conversation.answer || 'No response available',
									timestamp: conversation.created_at || conversation.timestamp || new Date().toISOString()
								};
							}
						}).flat(); // Flatten the array in case some conversations have multiple messages
						
						
						if (isLoadMore) {
							// For lazy loading, prepend new data to existing text (older messages at top)
							setText(prevText => [...conversationText, ...prevText]);
						} else {
							// For initial load, replace existing text
							setText(conversationText);
							setHasChatHistory(true); // Mark that we have chat history loaded
						}
						
						// Pagination is already updated before the API call
						
						
						// Let the useEffect handle scrolling - don't scroll here
						// This prevents conflicts between fetchChatHistory and useEffect scroll
					}
					
					// Check if there's more data to load
					const expectedBatchSize = historyEndLimit - historyStartLimit;
					setHasMoreHistory(data.length === expectedBatchSize);
				} else {
					setHasMoreHistory(false);
				}
			} else {
				setHasMoreHistory(false);
			}
			if (isLoadMore) {
				// Add a longer delay before setting isLoadingMore to false
				// This ensures the useEffect doesn't trigger scroll immediately
				setTimeout(() => {
					setIsLoadingMore(false);
					setJustFinishedLoadingMore(true);
					// Reset the flag after a longer delay
					setTimeout(() => {
						setJustFinishedLoadingMore(false);
					}, 3000);
				}, 500);
			} else {
				setHistoryLoading(false);
			}
		}).catch(error => {
			setHasMoreHistory(false);
			if (isLoadMore) {
				// Add a longer delay before setting isLoadingMore to false
				setTimeout(() => {
					setIsLoadingMore(false);
					setJustFinishedLoadingMore(true);
					// Reset the flag after a longer delay
					setTimeout(() => {
						setJustFinishedLoadingMore(false);
					}, 3000);
				}, 500);
			} else {
				setHistoryLoading(false);
			}
		});
	}, [companyId, lsData?.profile?.user?.id, historyStartLimit, historyEndLimit, getReportName, getReportPageCode]);

	const clearChatHistory = () => {
		// Show confirmation modal
		setShowClearHistoryConfirmation(true);
	};

	// Auto-fetch chat history when component mounts and becomes visible
	useEffect(() => {
		
		let hasFetched = false;
		
		const checkAndFetch = () => {
			// Check if component is visible by looking for the parent container
			const parentContainer = document.querySelector('.AITextGenWithPrompt_tab');
			const isVisible = parentContainer && !parentContainer.closest('div').classList.contains('d-none');
			
			
			if (isVisible && !hasFetched) {
				hasFetched = true;
				fetchChatHistory();
			}
			
			// REMOVED: Conflicting scroll call - let the useEffect handle scrolling
		};

		// Check immediately
		checkAndFetch();

		// Set up interval to check periodically (every 2 seconds)
		const interval = setInterval(checkAndFetch, 2000);

		// Clean up interval after 30 seconds to avoid infinite checking
		const timeout = setTimeout(() => {
			clearInterval(interval);
		}, 30000);

		return () => {
			clearInterval(interval);
			clearTimeout(timeout);
		};
	}, []);

	// Scroll to bottom on component mount (onload) - but NOT during Load More History
	useEffect(() => {
		// Multiple attempts with increasing delays to ensure DOM is ready
		setTimeout(() => {
			scrollToBottom(true);
		}, 500);
		
		setTimeout(() => {
			scrollToBottom(true);
		}, 1000);
		
		setTimeout(() => {
			scrollToBottom(true);
		}, 2000);
	}, []);

	// Scroll to bottom when text changes (new messages) - but NOT during Load More History
	useEffect(() => {
		// Check if user is at the top of the chat (indicating Load More History was used)
		const chatContainer = document.querySelector('.AIwidget-body_in');
		const isAtTop = chatContainer && chatContainer.scrollTop < 100;
		
		if (text.length > 0 && !isLoadingMore && !justFinishedLoadingMore && !isAtTop) {
			// Multiple attempts with delays to ensure scroll works
			setTimeout(() => {
				scrollToBottom();
			}, 300);
			
			setTimeout(() => {
				scrollToBottom();
			}, 800);
		}
	}, [text.length, isLoadingMore, justFinishedLoadingMore]);


	// Scroll detection for lazy loading in history panel
	useEffect(() => {
		const handleHistoryScroll = (e) => {
			const { scrollTop } = e.target;
			setScrollPosition(scrollTop);
			
			// Load more chat history when scrolling to the top of history panel
			if (scrollTop < 100 && hasMoreHistory && !isLoadingMore && !historyLoading && showHistory) {
				fetchChatHistory(true);
			}
		};

		// Add scroll listener to the history panel container
		const historyContainer = historyScrollRef.current;
		if (historyContainer) {
			historyContainer.addEventListener('scroll', handleHistoryScroll);
			return () => historyContainer.removeEventListener('scroll', handleHistoryScroll);
		}
	}, [hasMoreHistory, isLoadingMore, historyLoading, fetchChatHistory, showHistory]);

	// Read more/less functionality
	const toggleReadMore = (itemId) => {
		setExpandedItems(prev => {
			const newSet = new Set(prev);
			if (newSet.has(itemId)) {
				newSet.delete(itemId);
			} else {
				newSet.add(itemId);
			}
			return newSet;
		});
	};

	const truncateText = (text, maxLength = 200) => {
		if (text.length <= maxLength) return text;
		return text.substring(0, maxLength) + '...';
	};


	const handleClearHistoryConfirmation = async (confirmed) => {
		setShowClearHistoryConfirmation(false);
		
		if (!confirmed) {
			return;
		}

		try {
			const response = await AICopilotConfigService.deleteCopilotHistory(companyId, lsData?.profile?.user?.id, getReportName(), getReportPageCode());
			if (response.status === 200) {
				// Clear local history state
				setChatHistory([]);
				setHistoryStartLimit(0);
				setHistoryEndLimit(5);
				setHasMoreHistory(true);
				toast.success(t('ClearHistorySuccess.label'));
			} else {
				toast.error(t('ClearHistoryError.label'));
			}
		} catch (error) {
			toast.error(t('ClearHistoryError.label'));
		}
	};

	const loadMoreHistory = () => {
		if (!historyLoading && hasMoreHistory) {
			const newStartLimit = historyEndLimit;
			const newEndLimit = historyEndLimit + 5;

			setHistoryStartLimit(newStartLimit);
			setHistoryEndLimit(newEndLimit);

			// Fetch more history with updated limits
			setTimeout(() => {
				fetchChatHistory(true);
			}, 100);
		}
	};

	const handleHistoryScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;

		// Load more when user scrolls to bottom (with 50px threshold)
		if (scrollHeight - scrollTop - clientHeight < 50 && !historyLoading && hasMoreHistory) {
			loadMoreHistory();
		}
	};

	const formatHistoryDate = (dateString) => {
		const date = new Date(dateString);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return t('Today.label');
		} else if (date.toDateString() === yesterday.toDateString()) {
			return t('Yesterday.label');
		} else {
			return date.toLocaleDateString('en-US', {
				day: 'numeric',
				month: 'long',
				year: 'numeric'
			});
		}
	};

	const groupHistoryByDate = (historyData) => {
		const grouped = {};

		historyData.forEach(item => {
			const dateKey = formatHistoryDate(item.created_at || item.timestamp || new Date());
			if (!grouped[dateKey]) {
				grouped[dateKey] = [];
			}
			grouped[dateKey].push(item);
		});

		return grouped;
	};

	const fetchStreamedData = async (payload, type) => {
		try {
			// Create new AbortController for this request
			abortControllerRef.current = new AbortController();
			
			const url = ReportAIService.ReportContentGenStreamedAPIURL;

			setLastAPICallContext({
				api_name: url.substring(url.indexOf('/esg'), url.length),
				api_input: payload
			});
			const accessToken = localStorage.getItem("accessToken");
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': "Bearer " + accessToken
				},
				body: JSON.stringify(payload),
				signal: abortControllerRef.current.signal

			});

			const reader = response.body.getReader();
			let result = '';
			if (response.status === 200) {
				while (true) {
					const { done, value } = await reader.read();
					setLoading(false);
					if (done) break;
					result += new TextDecoder('utf-8').decode(value);
					let toSave = {
						question: payload.user_input,
						answer: result
					}
					// Use immutable state update instead of mutating the array
					setText(prevText => {
						// Create a new array with all previous items plus the new one
						const newText = [...prevText];
						// Reset chat history flag when user adds new content
						setHasChatHistory(false);
						// Replace the last item if it exists, otherwise add new item
						if (newText.length > 0) {
							newText[newText.length - 1] = toSave;
						} else {
							newText.push(toSave);
						}
						return newText;
					});
					setUserInput('');
					scrollToBottom();
				}
			} else {
				setLoading(false);
				return errorHandler(response);
			}
		} catch (error) {
			// Check if the error is due to abort
			if (error.name === 'AbortError') {
				toast.info(t('RequestCancelled.label'), {
					position: toast.POSITION.TOP_RIGHT,
				});
			} else {
			}
			setLoading(false);
		} finally {
			// Clear the abort controller reference
			abortControllerRef.current = null;
		}
	};

	const copyToClipboard = (html) => {
		if (navigator.clipboard && window.ClipboardItem) {
			const blob = new Blob([html], { type: 'text/html' });
			const data = [new window.ClipboardItem({ 'text/html': blob })];
			navigator.clipboard.write(data).then(() => {
				toast.info(t('Copied.label'));
			});
		} else {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = html || '';
			const text = tempDiv.innerText || tempDiv.textContent || '';
			navigator.clipboard.writeText(text);
			toast.info(t('Copied.label'));
		}
	};

	const fetchStreamedDataWithDataPoints = async (page, promptOverride) => {
		const type = page.type;
		const effectivePrompt = (promptOverride !== undefined && promptOverride !== null) ? promptOverride : userInput;

		// Check if this is an ESG Overview page
		if (isESGOverview(type)) {
			fetchESGOverviewAggregatedDataWithDataPoints(effectivePrompt);
			return;
		}

		// Check if this is a GHG Insights request - MUST be checked BEFORE ESG Highlights
		const isGHGInsights = isGhgLikeReportingStandard(reportingStandard) && (
			effectivePrompt.toLowerCase().includes('insights') ||
			page.type === ReportPageType.INSIGHTS ||
			page?.name?.toLowerCase().includes('insights') ||
			section?.name?.toLowerCase().includes('insights')
		);

		if (isGHGInsights) {
			fetchGHGInsightsDataWithMetrics(effectivePrompt);
			return;
		}

		setLoading(true);

		try {
			// Check if this is an ESG Highlights request
			if (effectivePrompt.toLowerCase().includes('esg highlights') || effectivePrompt.toLowerCase().includes('esg highlights section')) {
				// Parse user input for section and subsection selections
				const { selectedSections, selectedSubsections } = parseSectionSelectionsFromUserInput(effectivePrompt);

				// Use the new structured approach for ESG Highlights
				const structuredSections = await buildStructuredPayloadForESGHighlights(selectedSections, selectedSubsections);

				// Special case: If section is "About the report" and subsection is "ESG highlights"
				let sectionName = section.name;
				let subSectionName = page.name;

				if (section.name.toLowerCase().includes('about the report') && page.name.toLowerCase().includes('esg highlights')) {
					sectionName = 'ESG highlights';
					subSectionName = '';
				}

				// Special case: Set section_name as "DYNAMIC SECTION" when subsection is "Key Stakeholder Issues"
				if (page.name.toLowerCase().includes('key stakeholder issues')) {
					sectionName = 'DYNAMIC SECTION';
				}

				// Special case: Replace section name with "ESG Goals" when section is ESG Goals
				if (section.name.toLowerCase().includes('esg goals')) {
					sectionName = 'ESG Goals';
				}

				// Special case: When section name is "Our ESG Approach", set both section_name and sub_section_name as "ESG Goals"
				if (section.name.toLowerCase().includes('our esg approach')) {
					sectionName = 'ESG Goals';
					subSectionName = 'ESG Goals';
				}

				// Process section title for special cases
				let processedSectionTitle = section?.title || '';
				if (section.name.toLowerCase().includes('key stakeholder')) {
					processedSectionTitle = 'Key Stakeholder Issues';
				}

				// If section_name is "DYNAMIC SECTION", set section_title as page name
				if (sectionName === 'DYNAMIC SECTION') {
					processedSectionTitle = page.name;
				}



				const reportData = {
					job_id: getJobId(),
					company_name: companyName,
					section_name: sectionName,
					sub_section_name: subSectionName,
					industry: companyInfo?.industry?.name ?? '',
					country: companyInfo?.address?.country?.name ?? '',
					word_limit: processWordLimit(wordCount),
					company_id: companyId,
					section_id: Utils.parseAIConfigSectionIdFromReportPageType(type),
					role_id: activeNarratedBy,
					audience_id: activeAudience,
					language: language === 'en' ? 'en-us' : language,
					reporting_standard: reportingStandard ?? 'GRI',
					stream: true,
					year: periodStart && periodEnd && periodStart !== 'undefined' && periodEnd !== 'undefined' ? ReportingUtils.formatPeriodForAIPayload(periodStart, periodEnd) : `${periodYear}`,
					periodicity: regulationFrequency || 'ANNUAL',
					esg_frameworks: companyInfo?.esgFrameworks || '',
					company_info: companyInfo?.about || '',
					esg_goals: companyInfo?.esgGoals || '',
					section_description: section?.description || '',
					section_title: processedSectionTitle,
					supplementary_data: [{
						sections: structuredSections,
						is_return: true
					}],
					report_type: getReportType()
				};

				enrichReportDataQuantitativeValuesFromDatapoints(reportData);

				const fileData = selectedFile;
				const payload = {
					user_input: effectivePrompt,
					data: {
						user_id: lsData?.profile?.user?.id || 999, // Correct path: profile.user.id
						company_id: companyId,
						company_name: companyName,
						file_data: fileData,
						report_data: reportData,
					},
					context: "Reporting_Studio",
					language: language === 'en' ? 'en-us' : language,
					report_name: getReportName(),
					report_page: getReportPageCode()
				};



				setLoading(true);
				fetchStreamedData(payload, type);
			} else {
				// Use the original approach for other sections
				const topicData = JSON.parse(JSON.stringify(page.json));
				const { periodStart: validPeriodStart, periodEnd: validPeriodEnd, entities: validEntities } = getValidPeriodParams();
				const esgReq = ReportDataService.getESGSummary({
					standardId: regulationId,
					periodStart: validPeriodStart,
					periodEnd: validPeriodEnd,
					buIds: validEntities,
					topicIds: [topicData ? topicData.id : 0]
				});				
				const gdReq = GeneralDisclosureService.getGDQuestionAnswersByCode(
					`${periodYear}-01-01`,
					ReportingUtils.getGDQuestionCodesByPageType(type),
					validPeriodStart, validPeriodEnd
				);
				const [esgRes, gdRes] = await Promise.all([esgReq, gdReq]);				
				let esgPayload = ReportingUtils.mapESGSummaryDataToAIPayload(esgRes.data);
				const qualData = esgPayload.sections[0]?.supplementary_data[0]?.qualitative_metrics ?? [];
				populateWithGDQuestionAnswers(gdRes.data.questions, qualData);
				if (esgPayload.sections[0]?.supplementary_data[0]) {
					esgPayload.sections[0].supplementary_data[0].qualitative_metrics = qualData;
				}

				// Process section name for special cases
				let processedSectionName = section.name;

				// Special case: Set section_name as "DYNAMIC SECTION" when subsection is "Key Stakeholder Issues"
				if (page.name.toLowerCase().includes('key stakeholder issues')) {
					processedSectionName = 'DYNAMIC SECTION';
				}

				// Special case: Replace section name with "ESG Goals" when section is ESG Goals
				if (section.name.toLowerCase().includes('esg goals')) {
					processedSectionName = 'ESG Goals';
				}

				// Special case: When section name is "Our ESG Approach", set both section_name and sub_section_name as "ESG Goals"
				if (section.name.toLowerCase().includes('our esg approach')) {
					processedSectionName = 'ESG Goals';
				}

				// Process section title for special cases
				let processedSectionTitle = section?.title || '';
				if (section.name.toLowerCase().includes('key stakeholder')) {
					processedSectionTitle = 'Key Stakeholder Issues';
				}

				// If section_name is "DYNAMIC SECTION", set section_title as page name
				if (processedSectionName === 'DYNAMIC SECTION') {
					processedSectionTitle = page.name;
				}

				// Determine sub_section_name based on special cases
				let processedSubSectionName = page.name;
				if (section.name.toLowerCase().includes('our esg approach')) {
					processedSubSectionName = 'ESG Goals';
				}

				const reportData = {
					job_id: getJobId(),
					company_name: companyName,
					section_name: processedSectionName,
					sub_section_name: processedSubSectionName,
					industry: companyInfo?.industry?.name ?? '',
					country: companyInfo?.address?.country?.name ?? '',
					word_limit: processWordLimit(wordCount),
					supplementary_data: esgPayload.sections[0]?.supplementary_data ?? [],
					company_id: companyId,
					section_id: Utils.parseAIConfigSectionIdFromReportPageType(type),
					role_id: activeNarratedBy,
					audience_id: activeAudience,
					language: language === 'en' ? 'en-us' : language,
					reporting_standard: reportingStandard ?? 'GRI',
					stream: false,
					year: periodStart && periodEnd && periodStart !== 'undefined' && periodEnd !== 'undefined' ? ReportingUtils.formatPeriodForAIPayload(periodStart, periodEnd) : `${periodYear}`,
					periodicity: regulationFrequency || 'ANNUAL',
					esg_frameworks: companyInfo?.esgFrameworks || '',
					company_info: companyInfo?.about || '',
					esg_goals: companyInfo?.esgGoals || '',
					section_description: section?.description || '',
					section_title: processedSectionTitle,
					report_type: getReportType()
				};

				enrichReportDataQuantitativeValuesFromDatapoints(reportData);

				const fileData = selectedFile;
				const payload = {
					user_input: effectivePrompt,
					data: {
						user_id: lsData?.profile?.user?.id || 999, // Correct path: profile.user.id
						report_data: reportData,
						file_data: fileData,
						company_id: companyId,
						company_name: companyName,
					},
					context: "Reporting_Studio",
					language: language === 'en' ? 'en-us' : language,
					report_name: getReportName(),
					report_page: getReportPageCode()
				};

				setLoading(true);
				fetchStreamedData(payload, type);
			}
		} catch (err) {
			setLoading(false);
		}
	};

	const populateWithGDQuestionAnswers = (questions, qualitativeMetrics) => {
		const processQuestion = (questionData) => {
			if (!questionData || !questionData.answers || questionData.answers.length === 0) {
				return null;
			}
	
			const answersByYear = new Map();
			questionData.answers.forEach(a => {
				const year = moment(a.period).year();
				if (!answersByYear.has(year)) {
					answersByYear.set(year, []);
				}
				answersByYear.get(year).push(a.answer);
			});
	
			const questionAnswers = [];
			for (const [year, answers] of answersByYear.entries()) {
				questionAnswers.push({
					year: year,
					answer: answers.join(", ")
				});
			}
	
			if (questionAnswers.length > 0) {
				return {
					question: questionData.name?.trim(),
					answers: questionAnswers
				};
			}
			return null;
		};
	
		questions?.forEach(question => {
			const qualMetricData = {
				metric: question.name?.trim(),
				questions: []
			};
			
			const mainQuestionVO = processQuestion(question);
			if (mainQuestionVO) {
				qualMetricData.questions.push(mainQuestionVO);
			}
	
			question.children?.forEach(child => {
				const childQuestionVO = processQuestion(child);
				if (childQuestionVO) {
					qualMetricData.questions.push(childQuestionVO);
				}
			});
			
			qualitativeMetrics.push(qualMetricData);
		});
	};

	const fetchESGOverviewAggregatedDataWithDataPoints = (effectivePrompt) => {
		const promptForPayload = (effectivePrompt !== undefined && effectivePrompt !== null) ? effectivePrompt : userInput;
		setLoading(true);
		// Don't change the user input - keep the original text
		const { periodStart: validPeriodStart, periodEnd: validPeriodEnd, entities: validEntities } = getValidPeriodParams();
		ReportService.getESGOverviewPageData(regulationId, `${periodYear}-01-01`, +section.type, 
			validPeriodStart, validPeriodEnd, validEntities
		)
		.then(res => {
				setLoading(false);
				if (res.status !== 200) return;


				let esgPayload = ReportingUtils.mapESGSummaryDataToAIPayload(res.data);

				// Process section name for special cases
				let processedSectionName = section.name;

				// Special case: Set section_name as "DYNAMIC SECTION" when subsection is "Key Stakeholder Issues"
				const subsectionName = ReportingUtils.getSubsectionNameForType(page.type);
				if (subsectionName && subsectionName.toLowerCase().includes('key stakeholder issues')) {
					processedSectionName = 'DYNAMIC SECTION';
				}

				// Special case: Replace section name with "ESG Goals" when section is ESG Goals
				if (section.name.toLowerCase().includes('esg goals')) {
					processedSectionName = 'ESG Goals';
				}

				// Special case: When section name is "Our ESG Approach", set both section_name and sub_section_name as "ESG Goals"
				if (section.name.toLowerCase().includes('our esg approach')) {
					processedSectionName = 'ESG Goals';
				}

				// Process section title for special cases
				let processedSectionTitle = section?.title || '';
				if (section.name.toLowerCase().includes('key stakeholder')) {
					processedSectionTitle = 'Key Stakeholder Issues';
				}

				// If section_name is "DYNAMIC SECTION", set section_title as page name
				if (processedSectionName === 'DYNAMIC SECTION') {
					processedSectionTitle = page.name;
				}

				// Determine sub_section_name based on special cases
				let processedSubSectionName = ReportingUtils.getSubsectionNameForType(page.type);
				if (section.name.toLowerCase().includes('our esg approach')) {
					processedSubSectionName = 'ESG Goals';
				}

				const reportData = {
					job_id: getJobId(),
					section_name: processedSectionName,
					sub_section_name: processedSubSectionName,
					industry: companyInfo?.industry?.name ?? '',
					country: companyInfo?.address?.country?.name ?? '',
					word_limit: processWordLimit(wordCount),
					supplementary_data: esgPayload.sections[0]?.supplementary_data ?? [],
					section_id: Utils.parseAIConfigSectionIdFromReportPageType(page.type),
					role_id: activeNarratedBy,
					audience_id: activeAudience,
					stream: false,
					reporting_standard: reportingStandard ?? '',
					company_id: companyId,
					company_name: companyName,
					year: periodStart && periodEnd && periodStart !== 'undefined' && periodEnd !== 'undefined' ? ReportingUtils.formatPeriodForAIPayload(periodStart, periodEnd) : `${periodYear}`,
					periodicity: regulationFrequency || 'ANNUAL',
					esg_frameworks: companyInfo?.esgFrameworks || '',
					company_info: companyInfo?.about || '',
					esg_goals: companyInfo?.esgGoals || '',
					section_description: section?.description || '',
					section_title: processedSectionTitle,
					report_type: getReportType()
				};

				enrichReportDataQuantitativeValuesFromDatapoints(reportData);

				const fileData = selectedFile;
				const payload = {
					user_input: promptForPayload,
					data: {
						user_id: lsData?.profile?.user?.id || 999, // Correct path: profile.user.id
						report_data: reportData,
						file_data: fileData,
						company_id: companyId,
						company_name: companyName,
					},
					context: "Reporting_Studio",
					language: language === 'en' ? 'en-us' : language,
					report_name: getReportName(),
					report_page: getReportPageCode(),
				}
				setLoading(true);
				fetchStreamedData(payload, page.type);
			}).catch(err => {
				setLoading(false);
			});
	};

	const isESGOverview = (type) => {
		return type === ReportPageType.ENV_OVERVIEW
			|| type === ReportPageType.SOCIAL_OVERVIEW
			|| type === ReportPageType.GOV_OVERVIEW;
	};

	const getRefinedTextForMaterialTopic = () => {
		const esrsMappingIndex = materialTopic.mapping?.findIndex(m => m.standard === 'ESRS');
		let payload;
		if (esrsMappingIndex >= 0) {
			payload = {
				job_id: getJobId(),
				question: materialTopic.question?.trim() ?? '',
				frameworks: ['ESRS'],
				question_codes: { ESRS: materialTopic.mapping[esrsMappingIndex].frameworkCode },
				answer: materialTopic.answer?.trim() ?? '',
				language: language === 'en' ? 'en-us' : language
			};
		} else {
			payload = {
				job_id: getJobId(),
				company_name: companyName,
				industry: companyInfo?.industry?.name ?? '',
				materialtopic: materialTopic.materialtopic?.trim() ?? '',
				question: materialTopic.question?.trim() ?? '',
				guideline: materialTopic.guideline?.trim() ?? '',
				answer: materialTopic.answer?.trim() ?? '',
				language: language === 'en' ? 'en-us' : language
			};
		}
		setLoading(true);
		setLastAPICallContext({
			api_name: esrsMappingIndex >= 0 ? API_ROUTES.REPORT_AI.MATERIAL_TOPIC_ESRS : API_ROUTES.REPORT_AI.MATERIAL_TOPIC,
			api_input: payload
		});
		let req;
		if (esrsMappingIndex >= 0) {
			req = ReportAIService.getRefinedTextForMaterialTopicESRS(payload);
		} else {
			req = ReportAIService.getRefinedTextForMaterialTopic(payload);
		}
		req.then(res => {
			setLoading(false);
			if (res.status !== 200) return;
			
			// Don't override text if we have chat history loaded
			if (!hasChatHistory) {
				if (esrsMappingIndex >= 0) {
					setText(res.data.improved_answer);
				} else {
					setText(res.data.example_response);
					delete res.data.example_response;
				}
			}

			setRefinedText(res.data);
		})
			.catch(err => { setLoading(false); });
	};

	const fetchESGHighlightsDataWithMetrics = () => {
		if (location?.state?.esgData) {
			const payload = {
				job_id: getJobId(),
				company: {
					company_id: companyId,
					company_info: companyInfo?.about ?? '',
					company_name: companyName,
					country: companyInfo?.address?.country?.name ?? '',
					esg_frameworks: companyInfo?.esgFrameworks ?? '',
					esg_goals: companyInfo?.esgGoals ?? '',
					industry: companyInfo?.industry?.name ?? '',
					reporting_standard: reportingStandard ?? '',
					reporting_year: periodStart && periodEnd && periodStart !== 'undefined' && periodEnd !== 'undefined' ? ReportingUtils.formatPeriodForAIPayload(periodStart, periodEnd) : `${periodYear}`,
				},
				sections: location.state.esgData.sections,
				is_return: true,
				language: language === 'en' ? 'en-us' : language
			};
			setLoading(true);
			setLastAPICallContext({
				api_name: API_ROUTES.REPORT_AI.ESG_HIGHLIGHTS,
				api_input: payload
			});
			ReportAIService.generateTextForESGHighlights(payload)
				.then(res => {
					setLoading(false);
					if (res.status !== 200) return;



					// For ESG Highlights, wrap the data in the expected format
					// Don't override text if we have chat history loaded
					if (!hasChatHistory) {
						setText([{
							question: userInput || 'Generate ESG Highlights',
							answer: res.data
						}]);
					}
				})
				.catch(err => { setLoading(false); });
		} else {
			setLoading(true);
			const { periodStart: validPeriodStart, periodEnd: validPeriodEnd, entities: validEntities } = getValidPeriodParams();
			ReportDataService.getESGSummary({
				standardId: regulationId,
				periodStart: validPeriodStart,
				periodEnd: validPeriodEnd,
				buIds: validEntities
			}).then(res => {
					setLoading(false);
					if (res.status !== 200) return;

					let payload = ReportingUtils.mapESGSummaryDataToAIPayload(res.data);
					payload.job_id = getJobId();
					payload['report_type'] = getReportType();
					payload.company =  {
						company_id: companyId,
						company_info: companyInfo?.about ?? '',
						company_name: companyName,
						country: companyInfo?.address?.country?.name ?? '',
						esg_frameworks: companyInfo?.esgFrameworks ?? '',
						esg_goals: companyInfo?.esgGoals ?? '',
						industry: companyInfo?.industry?.name ?? '',
						reporting_standard: reportingStandard ?? '',
						reporting_year: periodStart && periodEnd && periodStart !== 'undefined' && periodEnd !== 'undefined' ? ReportingUtils.formatPeriodForAIPayload(periodStart, periodEnd) : `${periodYear}`,
					};
					payload.is_return = true;
					payload.language = language === 'en' ? 'en-us' : language;
					setLoading(true);
					setLastAPICallContext({
						api_name: API_ROUTES.REPORT_AI.ESG_HIGHLIGHTS,
						api_input: payload
					});
					ReportAIService.generateTextForESGHighlights(payload)
						.then(res => {
							setLoading(false);
							if (res.status !== 200) return;



							// For ESG Highlights, wrap the data in the expected format
							// Don't override text if we have chat history loaded
							if (!hasChatHistory) {
								setText([{
									question: userInput || 'Generate ESG Highlights',
									answer: res.data
								}]);
							}
						})
						.catch(err => { setLoading(false); });
				})
				.catch(err => {
					setLoading(false);
				});
		}
	};

	// New function for GHG Insights - similar to ESG highlights but for GHG reports.
	// It first calls the metric insights API and then uses that response inside supplementary_data
	// for the ESGAI insights generation API.
	const fetchGHGInsightsDataWithMetrics = (effectivePrompt) => {
		const promptForPayload = (effectivePrompt !== undefined && effectivePrompt !== null) ? effectivePrompt : userInput;
		console.log('[GHG Insights] Starting GHG Insights data fetch', {
			reportingStandard,
			page: page?.name,
			section: section?.name,
			timestamp: new Date().toISOString()
		});

		setLoading(true);
		const { periodStart: validPeriodStart, periodEnd: validPeriodEnd } = getValidPeriodParams();

		ReportDataService.getMetricInsights({
			type: reportingStandard || 'GHG',
			standardId: regulationId,
			periodStart: validPeriodStart,
			periodEnd: validPeriodEnd,
			rFreq: regulationFrequency || 'FINANCIAL_YEAR'
		})
			.then(res => {
				if (res.status !== 200) {
					setLoading(false);
					return;
				}

				// Get the metric insights response - this goes into supplementary_data
				const metricInsightsResponse = res.data || {};

				console.log('[GHG Insights] Metric insights response received', {
					metricInsightsResponse,
					timestamp: new Date().toISOString()
				});

				// Build the payload structure matching the ESG Highlights format
				// supplementary_data goes inside report_data, and report_data goes inside data object
				const reportData = {
					job_id: getJobId(),
					company_name: companyName,
					section_name: section?.name || 'ESG highlights',
					sub_section_name: '',
					industry: companyInfo?.industry?.name ?? '',
					country: companyInfo?.address?.country?.name ?? '',
					word_limit: processWordLimit(wordCount),
					company_id: companyId,
					section_id: page?.type ? Utils.parseAIConfigSectionIdFromReportPageType(page.type) : '',
					role_id: activeNarratedBy,
					audience_id: activeAudience,
					language: language === 'en' ? 'en-us' : language,
					reporting_standard: reportingStandard ?? 'GHG',
					stream: true,
					year: validPeriodStart && validPeriodEnd && validPeriodStart !== 'undefined' && validPeriodEnd !== 'undefined' 
						? ReportingUtils.formatPeriodForAIPayload(validPeriodStart, validPeriodEnd) 
						: `${periodYear}`,
					periodicity: regulationFrequency || 'ANNUAL',
					esg_frameworks: companyInfo?.esgFrameworks || '',
					company_info: companyInfo?.about || '',
					esg_goals: companyInfo?.esgGoals || '',
					section_description: section?.description || '',
					section_title: section?.title || '',
					report_type: getReportType(),
					supplementary_data: [metricInsightsResponse] // Metric insights response goes inside report_data
				};

				enrichReportDataQuantitativeValuesFromDatapoints(reportData);

				const fileData = selectedFile;
				const payload = {
					user_input: promptForPayload || 'Generate Insights',
					data: {
						user_id: lsData?.profile?.user?.id || 999,
						company_id: companyId,
						company_name: companyName,
						file_data: fileData,
						report_data: reportData // report_data goes inside data object
					},
					context: "Reporting_Studio",
					language: language === 'en' ? 'en-us' : language,
					report_name: getReportName(),
					report_page: getReportPageCode()
				};

				console.log('[GHG Insights] Final payload prepared for Copilot API', {
					payload,
					timestamp: new Date().toISOString()
				});

				// Use streaming API for copilot/ai_copilot endpoint
				const url = ReportAIService.ReportContentGenStreamedAPIURL;
				
				setLastAPICallContext({
					api_name: url.substring(url.indexOf('/copilot'), url.length),
					api_input: payload
				});

				// Create new AbortController for this request
				abortControllerRef.current = new AbortController();
				const accessToken = localStorage.getItem("accessToken");
				
				fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': "Bearer " + accessToken
					},
					body: JSON.stringify(payload),
					signal: abortControllerRef.current.signal
				})
					.then(async (response) => {
						if (response.status !== 200) {
							setLoading(false);
							return errorHandler(response);
						}

						const reader = response.body.getReader();
						let result = '';
						
						while (true) {
							const { done, value } = await reader.read();
							setLoading(false);
							if (done) break;
							
							result += new TextDecoder('utf-8').decode(value);
							let toSave = {
								question: payload.user_input || 'Generate Insights',
								answer: result
							};
							
							// Use immutable state update instead of mutating the array
							setText(prevText => {
								// Create a new array with all previous items plus the new one
								const newText = [...prevText];
								// Reset chat history flag when user adds new content
								setHasChatHistory(false);
								// Replace the last item if it exists, otherwise add new item
								if (newText.length > 0) {
									newText[newText.length - 1] = toSave;
								} else {
									newText.push(toSave);
								}
								return newText;
							});
							setUserInput('');
							scrollToBottom();
						}

						console.log('[GHG Insights] AI streaming response completed', {
							timestamp: new Date().toISOString()
						});
					})
					.catch(err => {
						// Check if the error is due to abort
						if (err.name === 'AbortError') {
							toast.info(t('RequestCancelled.label'), {
								position: toast.POSITION.TOP_RIGHT,
							});
						} else {
							console.error('[GHG Insights] AI generation failed', {
								error: err,
								timestamp: new Date().toISOString()
							});
						}
						setLoading(false);
					})
					.finally(() => {
						// Clear the abort controller reference
						abortControllerRef.current = null;
					});
			})
			.catch(err => {
				console.error('[GHG Insights] Metric insights data fetch failed', {
					error: err,
					timestamp: new Date().toISOString()
				});
				setLoading(false);
			});
	};

	const renderESGHighlightsResponse = () => {
		return (
			<div className='ESGHighlightsResponse_box01'>
				{
					loading
						? renderSpinnerLoader()
						:
						<div>
							{/* Display ESG Highlights as cards */}
							<div dangerouslySetInnerHTML={{ __html: convertESGHighlightsToHTML(text) }} />

							<div className='pt-3 px-2 pb-2'>
								<div className='responsive_qusCard justify-content-end'>
									<ul className='ESGAI_Clipboard-EditeBox'>
										<li>
											<button className='btn btn-secondary'
												onClick={() => onTransferToEditor(wrapContentWithItalic(convertESGHighlightsToHTML(text), true), null)}>
												{t('TransferToEditor.label')}
											</button>
										</li>
									</ul>
								</div>
							</div>
						</div>
				}
			</div>
		);
	};

	const errorHandler = (error) => {
		const _ = ReportingUtils.decodeErrorMessage(error?.status, '', t);
		toast.error(
			<div>
				<h6>{_.header}</h6>
				<div>{_.message}</div>
			</div>, {
			position: toast.POSITION.TOP_RIGHT,
			autoClose: false
		});
		return Promise.reject(error);
	};

	const isGenerateButtonDisabled = () => {
		if (loading) return true;

		return activeAudience?.length === 0 || activeNarratedBy?.length === 0 || (page?.type === ReportPageType.OTHERS && !description);
	};

	/** Returns the placeholder translation key for the prompt textarea. Extend for future cases (e.g. by reportingStandard or page type). */
	const getPromptPlaceholderKey = () => {
		if (page?.type === ReportPageType.GHG_MANAGEMENT_PERFORMANCE || reportingStandard === 'BRSR') {
			return 'RephrasePromptPlaceholder.label';
		}
		return 'AskQuestionPlaceholder.label';
	};

	const renderSpinnerLoader = () => {
		return (
			<div className='text-left'>
				<div class="chatloader-inner ball-pulse">
					<div></div>
					<div></div>
					<div></div>
				</div>
			</div>
		);
	};

	const getRefinedTextForBRSR = () => {
		const payload = {
			job_id: getJobId(),
			question: brsrData.question?.trim() ?? '',
			qualitative_answer: brsrData.answer?.trim() ?? ''
		};
		setLoading(true);
		ReportAIService.getBRSRRephrasedAnswer(payload)
			.then(res => {
				setLoading(false);
				if (res.status !== 200) return;

				// Don't override text if we have chat history loaded
				if (!hasChatHistory) {
					setText(res.data.rephrased_answer);
				}
				delete res.data.rephrased_answer;
				setRefinedText(res.data);
			})
			.catch(err => { setLoading(false); });
	};

	const getRefinedTextForContentBlock = () => {
		const payload = {
			job_id: getJobId(),
			question: '',
			qualitative_answer: descriptionData ?? ''
		};
		setLoading(true);
		ReportAIService.getBRSRRephrasedAnswer(payload)
			.then(res => {
				setLoading(false);
				if (res.status !== 200) return;

				// Don't override text if we have chat history loaded
				if (!hasChatHistory) {
					setText(res.data.rephrased_answer);
				}
				delete res.data.rephrased_answer;
				setRefinedText(res.data);
			})
			.catch(err => { setLoading(false); });
	};

	const refactorQuantMetrics = (quantitativeMetrics) => {
		quantitativeMetrics.forEach(metric => {
			metric.datapoints = metric.datapoints?.filter(dp => dp.values?.length > 0);
		});
		return quantitativeMetrics.filter(metric => metric.values?.length > 0 || metric.datapoints?.length > 0);
	};

	const refactorQualMetrics = (qualMetrics) => {
		return qualMetrics.filter(metric => metric.questions?.length > 0);
	};

	const createMarkup = (content) => {
		return { __html: content };
	};

	// Helper function to fix malformed JSON strings
	const fixAndParseJSON = (str) => {
		if (typeof str !== 'string') return str;



		try {
			// Extract objects using a more robust approach
			const objects = [];

			// Find all title patterns to locate objects
			const titleMatches = str.match(/'title':\s*'([^']+)'/g);
			if (titleMatches) {
				// For each title, extract the corresponding object
				titleMatches.forEach((titleMatch, index) => {
					try {
						// Find the start of this object (look for opening brace before the title)
						const titleStart = str.indexOf(titleMatch);
						const objectStart = str.lastIndexOf('{', titleStart);

						if (objectStart !== -1) {
							// Find the end of this object (look for closing brace after the title)
							let braceCount = 0;
							let objectEnd = -1;

							for (let i = objectStart; i < str.length; i++) {
								if (str[i] === '{') braceCount++;
								if (str[i] === '}') {
									braceCount--;
									if (braceCount === 0) {
										objectEnd = i + 1;
										break;
									}
								}
							}

							if (objectEnd !== -1) {
								const objectStr = str.substring(objectStart, objectEnd);

								// Clean up the object string
								let cleanObjectStr = objectStr
									.replace(/'/g, '"')  // Replace single quotes with double quotes
									.replace(/AITextGenWithPrompt\.js:\d+/g, '')  // Remove debugging artifacts
									.replace(/\s+/g, ' ')  // Normalize whitespace
									.trim();

								// Try to parse the cleaned object
								const obj = JSON.parse(cleanObjectStr);
								if (obj.title && obj.Highlight_Value && obj.Description) {
									objects.push(obj);
								}
							}
						}
					} catch (e) {
						// Silently handle parsing errors
					}
				});
			}

			if (objects.length > 0) {
				return { AnalysisResult: objects };
			}

			// Fallback: try the original approach
			const fixedStr = str.replace(/'/g, '"');
			return JSON.parse(fixedStr);
		} catch (e) {
			return str;
		}
	};

	// Wrap content with italic styling for transfer to editor
	const wrapContentWithItalic = (content, isESGData) => {
		if (!content) return content;
		if (isESGData) {
			return `<p><em>${content}</em></p>`;
		}
		const textContent = typeof content === 'string' ? content : String(content);
		return `<p><em>${textContent.replace(/\n/g, '<br>')}</em></p>`;
	};

	// Convert ESG Highlights JSON to HTML cards
	const convertESGHighlightsToHTML = (esgData) => {

		// Check for different possible structures
		let analysisResult = null;
		if (esgData && esgData.AnalysisResult && Array.isArray(esgData.AnalysisResult)) {
			analysisResult = esgData.AnalysisResult;
		} else if (esgData && Array.isArray(esgData)) {
			analysisResult = esgData;
		} else if (esgData && typeof esgData === 'object' && Object.keys(esgData).length > 0) {
			// Try to find AnalysisResult in nested structure
			const keys = Object.keys(esgData);
			for (const key of keys) {
				if (esgData[key] && Array.isArray(esgData[key])) {
					analysisResult = esgData[key];
					break;
				}
			}
		}

		if (!analysisResult || !Array.isArray(analysisResult)) {
			return '';
		}

		const cardsHTML = analysisResult.map((item, index) => {
			// Determine color based on percentage value
			const percentage = item.Highlight_Value;
			const isPositive = percentage.includes('+') || (percentage.includes('%') && !percentage.includes('-'));
			const colorClass = isPositive ? 'text-success' : 'text-danger';

			// Ensure percentage has % symbol
			let displayValue = item.Highlight_Value;
			if (!displayValue.includes('%')) {
				displayValue = displayValue + '%';
			}

			return `
				<div style="flex: 0 0 auto; min-width: 280px; max-width: 350px; margin-bottom: 1rem;">
					<div style="border: 2px solid #e5e7eb; border-radius: 8px; background: white; padding: 1.5rem; min-height: 200px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
						<div style="text-align: center; display: flex; flex-direction: column; justify-content: space-between; height: 100%;">
							<div style="margin-bottom: 1rem;">
								<span style="font-size: 2.5rem; font-weight: bold; color: ${isPositive ? '#059669' : '#dc2626'}; display: inline-block;">${displayValue}</span>
							</div>
							<div style="flex-grow: 1; display: flex; flex-direction: column; justify-content: center;">
								<h6 style="font-size: 1rem; font-weight: bold; line-height: 1.4; color: #374151; margin-bottom: 1rem; white-space: normal; word-wrap: break-word;">${item.title}</h6>
								<p style="font-size: 0.875rem; line-height: 1.5; color: #6b7280; margin-bottom: 0; white-space: normal; word-wrap: break-word; overflow-wrap: break-word;">${item.Description}</p>
							</div>
						</div>
					</div>
				</div>
			`;
		}).join('');

		return `
			<div class="container-fluid px-0" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
				<div style="display: flex; flex-wrap: wrap; gap: 1rem; justify-content: flex-start;">
					${cardsHTML}
				</div>
			</div>
		`;
	};

	const populateCaseStudies = (cbRes, caseStudies) => {
		cbRes?.forEach(cb => {
			caseStudies.push({
				Title: cb.name,
				Topics: cb.topic?.name ?? '',
				About: cb.about,
				Description: cb.description,
				year: moment(cb.period).year()
			});
		});
	};

	// New utility function to automatically populate and structure payload based on selected sections
	const buildStructuredPayloadForESGHighlights = async (selectedSections = [], selectedSubsections = {}) => {
		const structuredSections = [];

		// Get the current report data from context or props
		// This should be passed from the parent component or available in context
		const reportData = report || window.report || null;



		if (!reportData || !reportData.sections) {
			return [];
		}

		// Map section types to their corresponding section keys
		const sectionTypeMapping = {
			[ReportSection.ENVIRONMENT]: 'environment',
			[ReportSection.SOCIAL]: 'social',
			[ReportSection.GOVERNANCE]: 'governance'
		};

		// Filter sections based on user selection or include all ESG sections
		const sectionsToProcess = reportData.sections.filter(section => {
			const sectionKey = sectionTypeMapping[section.type];
			if (!sectionKey) return false;

			// If specific sections are selected, only include those
			if (selectedSections.length > 0) {
				return selectedSections.includes(sectionKey);
			}

			// Otherwise include all ESG sections
			return true;
		});

		for (const section of sectionsToProcess) {
			const sectionKey = sectionTypeMapping[section.type];
			if (!sectionKey) continue;

			const sectionData = {
				section: section.name,
				sectionId: section.id || null,
				sectionType: section.type,
				pageId: null,
				pageType: 0,
				word_limit: 0,
				metrics: null,
				subsections: [],
				supplementary_data: []
			};

			// Get subsections (pages) for this section
			const subsectionsToProcess = section.pages?.filter(page => {
				// Skip overview pages
				if (page.type === ReportPageType.ENV_OVERVIEW ||
					page.type === ReportPageType.SOCIAL_OVERVIEW ||
					page.type === ReportPageType.GOV_OVERVIEW) {
					return false;
				}

				// If specific subsections are selected for this section, filter by them
				if (selectedSubsections[sectionKey] && selectedSubsections[sectionKey].length > 0) {
					const pageNameLower = page.name.toLowerCase();
					return selectedSubsections[sectionKey].some(selectedSub =>
						pageNameLower.includes(selectedSub.toLowerCase())
					);
				}

				// Otherwise include all subsections
				return true;
			}) || [];

			// Process each subsection
			for (const subsection of subsectionsToProcess) {
				const subsectionData = {
					sub_section_name: subsection.name,
					sectionId: section.id || null,
					sectionType: section.type,
					pageId: subsection.id || null,
					pageType: subsection.type,
					word_limit: 600 // Default word limit, can be made configurable
				};

				// Fetch data for this subsection
				const topicData = JSON.parse(JSON.stringify(subsection.json || {}));
				const { periodStart: validPeriodStart, periodEnd: validPeriodEnd, entities: validEntities } = getValidPeriodParams();
				const esgReq = ReportDataService.getESGSummary({
					standardId: regulationId,
					periodStart: validPeriodStart,
					periodEnd: validPeriodEnd,
					buIds: validEntities,
					topicIds: [topicData ? topicData.id : 0]
				});					
				const gdReq = GeneralDisclosureService.getGDQuestionAnswersByCode(
					`${periodYear}-01-01`,
					ReportingUtils.getGDQuestionCodesByPageType(subsection.type),
					validPeriodStart, validPeriodEnd
				);
				try {
					const [esgRes, gdRes] = await Promise.all([esgReq, gdReq]);
					let esgPayload = ReportingUtils.mapESGSummaryDataToAIPayload(esgRes.data);
					const qualData = esgPayload.sections[0]?.supplementary_data[0]?.qualitative_metrics ?? [];
					populateWithGDQuestionAnswers(gdRes.data.questions, qualData);
					if (esgPayload.sections[0]?.supplementary_data[0]?.qualitative_metrics) {
						esgPayload.sections[0].supplementary_data[0].qualitative_metrics = qualData;
					}
					sectionData.subsections.push(subsectionData);
					sectionData.supplementary_data.push(esgPayload.sections[0]?.supplementary_data[0] ?? []);
				} catch (error) {
					// Add subsection with empty data if there's an error
					sectionData.subsections.push(subsectionData);
					sectionData.supplementary_data.push({
						topic: subsection.name,
						qualitative_metrics: [],
						quantitative_metrics: [],
						case_studies: []
					});
				}
			}

			structuredSections.push(sectionData);
		}



		return structuredSections;
	};

	// Helper function to parse user input for section and subsection selections
	const parseSectionSelectionsFromUserInput = (input) => {
		const selectedSections = [];
		const selectedSubsections = {};

		const inputLower = input.toLowerCase();

		// Check for section selections
		if (inputLower.includes('environment') || inputLower.includes('environmental')) {
			selectedSections.push('environment');
		}
		if (inputLower.includes('social')) {
			selectedSections.push('social');
		}
		if (inputLower.includes('governance')) {
			selectedSections.push('governance');
		}

		// Check for specific subsection selections - now more flexible to match actual page names
		const subsectionKeywords = {
			'energy': 'environment',
			'emission': 'environment',
			'carbon': 'environment',
			'waste': 'environment',
			'water': 'environment',
			'customers': 'social',
			'community': 'social',
			'employees': 'social',
			'suppliers': 'social',
			'board': 'governance',
			'human rights': 'governance',
			'ethics': 'governance',
			'tax': 'governance',
			'esg governance': 'governance',
			'business continuity': 'governance',
			'disaster recovery': 'governance'
		};

		Object.entries(subsectionKeywords).forEach(([keyword, section]) => {
			if (inputLower.includes(keyword)) {
				if (!selectedSubsections[section]) {
					selectedSubsections[section] = [];
				}

				// Add the keyword as a search term - the actual matching will be done by page name
				if (!selectedSubsections[section].includes(keyword)) {
					selectedSubsections[section].push(keyword);
				}
			}
		});

		return { selectedSections, selectedSubsections };
	};



	const onEnterPress = (e) => {
		if (e.keyCode == 13 && e.shiftKey == false) {
			e.preventDefault();
			generateConfigDependentText();
		}
	};

	const getFiles = () => {
		ReportAIService.getFilesExtraction(companyId)
			.then(res => {
				setUploadedFiles(res.data.files ?? []);
			})
			.catch((error) => {
			});
	}

	const handleFileSelect = (event) => {
		const fileUploaded = event.target.files[0];
		if (Utils.contentExtractionFileExceedsMaxSize(fileUploaded)) {
			fileInputRef.current.value = null;
			toast.warning(t('UploadFilesLessThan5MB.label'), {
				position: toast.POSITION.TOP_RIGHT,
				autoClose: 3000
			});
		} else {
			setFileUploader(fileUploaded);
			uploadFiles(fileUploaded);
		}
	};

	const uploadFiles = (file) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("company_id", companyId);
		formData.append("company_name", companyName);
		setLoading(true);
		ReportAIService.uploadFileForExtraction(formData)
			.then(res => {
				setLoading(false);
				setSelectedFile(res.data.metadata);
				getFiles();
			})
			.catch((error) => {
				setLoading(false);
			});
	}

	const scrollToBottom = (force = false) => {
		// Only scroll the widget's internal containers - do NOT use scrollIntoView
		// as it scrolls the document/page and causes the reporting page to jump
		const chatContainer = document.querySelector('.AIwidget-body_in');
		if (chatContainer) {
			const delay = force ? 100 : 50;
			setTimeout(() => {
				chatContainer.scrollTop = chatContainer.scrollHeight;
			}, delay);
		}

		const parentContainer = document.querySelector('.Genwidget-body');
		if (parentContainer) {
			setTimeout(() => {
				parentContainer.scrollTop = parentContainer.scrollHeight;
			}, force ? 150 : 100);
		}
	};

	// Function to stop the current API request
	const stopRequest = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			setLoading(false);
		}
	};

	const getReportType = () => {
		return  report?.basis ?? (entities?.length > 0 ? ReportBasis.ENTITY : ReportBasis.ORG);
	};

	const getNarrativeText = (page, withGraphs = false) => {
		if (getReportType() === ReportBasis.ORG) {
			if (withGraphs) {
				return t("GenerateOrgNarrativeSectionWithGraphs.label", { section: page.name });
			} else {
				return t("GenerateOrgNarrativeSection.label", { section: page.name });
			}			
		} else {
			if (withGraphs) {
				return t("GenerateEntityNarrativeSectionWithGraphs.label", { section: page.name });
			} else {
				return t("GenerateEntityNarrativeSection.label", { section: page.name });
			}	
		}
	};

	return (
		<div id='AiTextGenWithPrompt' className='1' data-testid="AiTextGenWithPrompt">

			<div className='Genwidget-body'>

				<div className='AIwidget-body_in 3'>
					{
						text.length == 0 && !loading &&
						<div className='AIwidget-Welcomebox '>
							<div className='AIwidgetWEl01'>
								<img src={esg_green} />
								<h4>{t("ESGAI.label")}</h4>
								<p>
									{t("AskQESGFile.label")}
								</p>
							</div>
						</div>
					}
					{
						text.length == 0 && !loading &&
						<div className='AIwidget_start_Qas'>
							<ul>
								{/* Check if this is an ESG section (Environmental, Social, Governance) */}
								{(section.name.toLowerCase().includes('environment') ||
									section.name.toLowerCase().includes('social') ||
									section.name.toLowerCase().includes('governance')) ? (
									<>
										<li 
											onClick={() => setUserInput(getNarrativeText(page))}>
											{getNarrativeText(page)}
										</li>
										<li 
											onClick={() => setUserInput(getNarrativeText(page, true))}>
											{getNarrativeText(page, true)}
										</li>
									</>
								) : (
									<li 
										onClick={() => setUserInput('Generate ' + page.name)}>
										{'Generate ' + page.name}
									</li>
								)}
							</ul>
						</div>
					}
					<div className='GenwidgetChat'>
						{/* Loading indicator for lazy loading */}
						{isLoadingMore && (
							<div className='lazy-loading-indicator'>
								<div className='spinner-border spinner-border-sm' role='status'>
									<span className='visually-hidden'>Loading more chat history...</span>
								</div>
								<span className='ms-2'>{t('LoadingMoreHistory.label')}</span>
							</div>
						)}
						
						{/* Show "Load More" button if there's more history and not loading */}
						{hasMoreHistory && !isLoadingMore && text.length > 0 && (
							<div className='load-more-container'>
								<button 
									className='btn btn-outline-primary btn-sm'
									onClick={() => fetchChatHistory(true)}
									disabled={historyLoading}
								>
									{historyLoading ? (
										<>
											<span className='spinner-border spinner-border-sm me-2' role='status'></span>
											{t('LoadingMoreHistory.label')}
										</>
									) : (
										t('LoadMoreHistory.label')
									)}
								</button>
							</div>
						)}
						
						{/* Show "No more history" message if no more data */}
						{!hasMoreHistory && text.length > 0 && (
							<div className='no-more-history'>
								<small className='text-muted'>{t('NoMoreHistory.label')}</small>
							</div>
						)}
						
						{
							text?.map((txt, tIndex) => {
								// Check if we need to show a date separator
								const showDateSeparator = tIndex === 0 || 
									(txt.timestamp && text[tIndex - 1]?.timestamp && 
									new Date(txt.timestamp).toDateString() !== new Date(text[tIndex - 1].timestamp).toDateString());
								
								return (
									<React.Fragment key={tIndex}>
										{/* Date separator */}
										{showDateSeparator && txt.timestamp && (
											<div className='chat-date-separator' style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												margin: '20px 0',
												position: 'relative'
											}}>
												<div className='date-line' style={{
													flex: 1,
													height: '1px',
													backgroundColor: 'rgba(0, 0, 0, 0.1)'
												}}></div>
												<span className='date-text' style={{
													padding: '0 15px',
													fontSize: '12px',
													fontWeight: '600',
													color: '#64748b',
													textTransform: 'uppercase',
													letterSpacing: '0.5px'
												}}>
													{formatHistoryDate(txt.timestamp)}
												</span>
												<div className='date-line' style={{
													flex: 1,
													height: '1px',
													backgroundColor: 'rgba(0, 0, 0, 0.1)'
												}}></div>
											</div>
										)}
										
										<div className='GenwidgetChat_QA_ASw'>
											<div className='Genwidgetchat_QA'>
										<div className='Genwidgetchat_QAChild'>
											<div className='Genwidgetchat_QAChild_in'>
												{(() => {
													// Read more/less functionality for questions
													const isQuestionExpanded = expandedItems.has(`question-${tIndex}`);
													const shouldTruncateQuestion = txt.question && txt.question.length > 150;
													const displayQuestion = shouldTruncateQuestion && !isQuestionExpanded ? truncateText(txt.question, 150) : txt.question;
													
													return (
														<div>
															<p>{displayQuestion}</p>
															{shouldTruncateQuestion && (
																<button 
																	className='btn btn-link btn-sm p-0 text-primary'
																	onClick={() => toggleReadMore(`question-${tIndex}`)}
																>
																	{isQuestionExpanded ? t('ReadLess.label') : t('ReadMore.label')}
																</button>
															)}
														</div>
													);
												})()}
											</div>
										</div>
									</div>
									<div className='Genwidgetchat_ASw'>
										<div className='Genwidgetchat_ASwChild'>
											{/* Check if this is ESG Highlights data and render as cards */}
											{(() => {
												// Try to parse if it's a string
												let parsedAnswer = fixAndParseJSON(txt.answer);

												// More flexible ESG data detection
												const isESGData = parsedAnswer && (
													(parsedAnswer.AnalysisResult && Array.isArray(parsedAnswer.AnalysisResult)) ||
													(Array.isArray(parsedAnswer) && parsedAnswer.length > 0 && parsedAnswer[0].title) ||
													(typeof parsedAnswer === 'object' && Object.keys(parsedAnswer).some(key =>
														Array.isArray(parsedAnswer[key]) && parsedAnswer[key].length > 0 && parsedAnswer[key][0].title
													))
												);

												if (isESGData) {
													return <div dangerouslySetInnerHTML={{ __html: convertESGHighlightsToHTML(parsedAnswer) }} />;
												} else {
													// Read more/less functionality for regular text
													const isExpanded = expandedItems.has(tIndex);
													const shouldTruncate = txt.answer && txt.answer.length > 200;
													const displayText = shouldTruncate && !isExpanded ? truncateText(txt.answer, 200) : txt.answer;
													
													return (
														<div>
															<p className='mb-1' dangerouslySetInnerHTML={createMarkup(displayText ?? '')}></p>
															{shouldTruncate && (
																<button 
																	className='btn btn-link btn-sm p-0 text-primary'
																	onClick={() => toggleReadMore(tIndex)}
																>
																	{isExpanded ? t('ReadLess.label') : t('ReadMore.label')}
																</button>
															)}
														</div>
													);
												}
											})()}
										</div>
										<div className='Genwidgetchat_ASwChild_in02'>
											<ul>
												{/* <li>
													<span><BiLike /></span>
												</li>
												<li>
													<span><BiDislike /></span>
												</li> */}
												<li>
													<OverlayTrigger
														placement="top"
														overlay={Utils.renderTooltip(t('CopyContent.label'))}>
														<span><BiCopy onClick={() => {
															// Try to parse if it's a string
															let parsedAnswer = fixAndParseJSON(txt.answer);

															const isESGData = parsedAnswer && (
																(parsedAnswer.AnalysisResult && Array.isArray(parsedAnswer.AnalysisResult)) ||
																(Array.isArray(parsedAnswer) && parsedAnswer.length > 0 && parsedAnswer[0].title) ||
																(typeof parsedAnswer === 'object' && Object.keys(parsedAnswer).some(key =>
																	Array.isArray(parsedAnswer[key]) && parsedAnswer[key].length > 0 && parsedAnswer[key][0].title
																))
															);
															copyToClipboard(isESGData ? convertESGHighlightsToHTML(parsedAnswer) : txt.answer);
														}} /></span>
													</OverlayTrigger>
												</li>
												<li>
													<OverlayTrigger
														placement="top"
														overlay={Utils.renderTooltip(t('TransferToEditor.label'))}>
														<span><BiTransferAlt onClick={() => {
															// Try to parse if it's a string
															let parsedAnswer = fixAndParseJSON(txt.answer);

															const isESGData = parsedAnswer && (
																(parsedAnswer.AnalysisResult && Array.isArray(parsedAnswer.AnalysisResult)) ||
																(Array.isArray(parsedAnswer) && parsedAnswer.length > 0 && parsedAnswer[0].title) ||
																(typeof parsedAnswer === 'object' && Object.keys(parsedAnswer).some(key =>
																	Array.isArray(parsedAnswer[key]) && parsedAnswer[key].length > 0 && parsedAnswer[key][0].title
																))
															);
															onTransferToEditor(wrapContentWithItalic(isESGData ? convertESGHighlightsToHTML(parsedAnswer) : txt.answer, isESGData), null);
														}} /></span>
													</OverlayTrigger>
												</li>
											</ul>

										</div>
									</div>
								</div>
							</React.Fragment>
						);
					})}
					</div>
					{loading && type === 'REPORTING_STUDIO' &&
						<div className='GenwidgetChat'>
							<div className='GenwidgetChat_QA_ASw'>
								<div className='Genwidgetchat_QA'>
									<div className='Genwidgetchat_QAChild'>
										<div className='Genwidgetchat_QAChild_in'>
											<p>{loadingPrompt || userInput}</p>
										</div>
									</div>
								</div>
								<div className='Genwidgetchat_ASw'>
									<div className='Genwidgetchat_ASwChild'>
										{renderSpinnerLoader()}
									</div>
								</div>
							</div>
						</div>
					}
					<div ref={chatEndRef} />
				</div>
				<div className='Genwidget-footer'>
					{

						<div className='Generate_Draft_previewbox'>
							{
								selectedFile != null &&
								<div className='Gen_attachedBox'>
									<div className='attached_pill'>
										<span className='attachedFile_icon'>
											<BsFileEarmarkText />
										</span>
										<div className='Gen_attachedBox_name'>
											{selectedFile.filename}
										</div>
										<span className='attachedFile_close cursor' onClick={() => setSelectedFile(null)}>
											<BiX className='cursor' />
										</span>
									</div>
								</div>
							}

							<textarea
								className='form-control autosize'
								placeholder={t(getPromptPlaceholderKey())}
								rows={2}
								ref={el => {
									if (el) {
										el.style.height = 'auto';
										el.style.height = el.scrollHeight + 'px';
									}
								}}
								onInput={e => {
									const el = e.target;
									el.style.height = 'auto';
									el.style.height = el.scrollHeight + 'px';
								}} value={userInput}
								onChange={(e) => setUserInput(e.target.value)}
								onKeyDown={(e) => onEnterPress(e)}
								disabled={isGenerateButtonDisabled()}>
							</textarea>
							{(userInput || '').trim().length > 0 && (page?.type === ReportPageType.GHG_MANAGEMENT_PERFORMANCE || reportingStandard === 'BRSR') && (
								<div className='RephraseThisText_option mt-1'>
									<button
										type='button'
										className='btn btn-link btn-sm p-0 text-primary text-decoration-none'
										onClick={() => {
											const newPrompt = (userInput || '').trim() + ' - Rephrase this';
											setUserInput(newPrompt);
											generateConfigDependentText(newPrompt);
										}}
										disabled={isGenerateButtonDisabled()}>
										{t('RephraseThisText.label')}
									</button>
								</div>
							)}
							<div className='GenwidgetDraftBtn'>
								<div className='GenwidgetDraftBtn_in01'>
									<div className='btn-Prompts'>
										<OverlayTrigger
											placement="top"
											overlay={Utils.renderTooltip(t('Settings.label'))}>
											<span onClick={() => setShowSettings(true)}>
												<AiOutlineSetting />
											</span>
										</OverlayTrigger>
									</div>
									{text.length > 0 && (
										<div className='btn-Prompts'>
											<OverlayTrigger
												placement="top"
												overlay={Utils.renderTooltip(t('ClearHistory.label'))}>
												<span onClick={clearChatHistory} style={{ color: '#000', cursor: 'pointer' }}>
													<BsTrash3 />
												</span>
											</OverlayTrigger>
										</div>
									)}
									<div>
										<Dropdown>
											<Dropdown.Toggle variant="Prompts" id="dropdown-basic">
												<OverlayTrigger
													placement="top"
													overlay={Utils.renderTooltip(t('AddAndUploadFile.label'))}>
													<span>
														<BsPlusLg />
													</span>
												</OverlayTrigger>
											</Dropdown.Toggle>
											<Dropdown.Menu>
												<Dropdown.Item href="#/action-1">
													<input type="file" id="fileInput" style={{ display: 'none' }}
														onChange={(e) => handleFileSelect(e)}
														onClick={(e) => {
															e.target.value = null;
														}}
														ref={fileInputRef} />
													<label className='' htmlFor="fileInput">
														<BsUpload disabled={isGenerateButtonDisabled()} className='me-2' /> {t('Upload.label')}
													</label>
												</Dropdown.Item>
												{/* <Dropdown.Item href="#/action-2" onClick={() => setShowFiles(true)}>
													<BsPaperclip className='me-2' /> {t('AttachFiles.label')}
												</Dropdown.Item> */}
											</Dropdown.Menu>
										</Dropdown>
									</div>
								</div>
								
								<div>
									{loading ? (
										<OverlayTrigger
											placement="top"
											overlay={Utils.renderTooltip(t('StopRequest.label'))}>
											<span className='btn-GenChat btn-stop' 
												onClick={stopRequest}>
												<IoStop className='btn-Chat' />
											</span>
										</OverlayTrigger>
									) : (
										<OverlayTrigger
											placement="top"
											overlay={Utils.renderTooltip(t('Send.label'))}>
											<span className='btn-GenChat' disabled={isGenerateButtonDisabled()}
												onClick={() => generateConfigDependentText()}>
												<MdSend className='btn-Chat' />
											</span>
										</OverlayTrigger>
									)}
								</div>
							</div>
						</div>
					}


				</div>
			</div>
			<div className={(showSettings || showFiles || showHistory) ? 'AiTextGen_popupsection' : 'AiTextGen_popupsection d-none'}>
				<div className='AiTextGen_Privacybox d-none'>
					<div className='AiTextGen_PrivacyboxHead'>
						<h5>{t('Privacy.label')}</h5>
						<span><BsXLg /></span>
					</div>
					<div className='AiTextGen_PrivacyboxBody'>
						<p>{t('Feedback.label')} @ <a href="mailto:copilot@ecodrisil.com">copilot@ecodrisil.com</a></p>
					</div>
				</div>

				<div className={showSettings ? 'AiTextGen_SettingCard' : 'AiTextGen_SettingCard d-none'}>
					<div className='AiTextGen_SettingCardHead'>
						<h5>Settings</h5>
						<span onClick={() => setShowSettings(false)}><BsXLg /></span>
					</div>
					<div className='AiTextGen_SettingCardBody'>
						<div className='AiTextGenPrompt_card'>
							<h6>{t("NarratedBy.label")}</h6>
							<ul>
								{
									narratedBy?.map((p, pIndex) =>
										<li>
											<div class="form-check">
												<input class="form-check-input" type="checkbox" value={p.type} id={`checkbuttonN-${p.id}-${pIndex}`}
													checked={activeNarratedBy?.indexOf(p.type) >= 0}
													onChange={(e) => setActiveNarratedBy(p.type)} />
												<label class="form-check-label" for={`checkbuttonN-${p.id}-${pIndex}`}>
													{p.name}
												</label>
											</div>
										</li>
									)
								}
							</ul>
						</div>
						<div className='AiTextGenPrompt_card'>
							<h6>{t("Audience.label")}</h6>
							<ul>
								{
									audience?.map((p, pIndex) =>
										<li>
											<div class="form-check">
												<input class="form-check-input" type="checkbox" value={p.type} id={`checkbuttonA-${p.id}-${pIndex}`}
													checked={activeAudience?.indexOf(p.type) >= 0}
													onChange={(e) => setActiveAudience(p.type)} />
												<label class="form-check-label" for={`checkbuttonA-${p.id}-${pIndex}`}>
													{p.name}
												</label>
											</div>
										</li>
									)
								}
							</ul>
						</div>

						<div className='AiTextGenPrompt_card'>
							<h6>{t("Length.label")}</h6>
							<ul>
								<li>
									<div class="form-check">
										<input class="form-check-input" type="checkbox" name={t('Short.label')}
											value={'SHORT'} id='configButtonShort' checked={wordCount === 'SHORT'} toolTip={`150 to 200 words`}
											onChange={(e) => setWordCount('SHORT')} />
										<label class="form-check-label" for='configButtonShort'>
											{t('Short.label')}
										</label>
									</div>
								</li>
								<li>
									<div class="form-check">
										<input class="form-check-input" type="checkbox" name={t('Medium.label')}
											value={'SHORT'} id='configButtonMedium' checked={wordCount === 'MEDIUM'} toolTip={`200 to 350 words`}
											onChange={(e) => setWordCount('MEDIUM')} />
										<label class="form-check-label" for='configButtonMedium'>
											{t('Medium.label')}
										</label>
									</div>
								</li>
								<li>
									<div class="form-check">
										<input class="form-check-input" type="checkbox" name={t('Long.label')}
											value={'LONG'} id='configButtonLong' checked={wordCount === 'LONG'} toolTip={`300 to 500 words`}
											onChange={(e) => setWordCount('LONG')} />
										<label class="form-check-label" for='configButtonLong'>
											{t('Long.label')}
										</label>
									</div>
								</li>
								<li>
									<div class="form-check">
										<input class="form-check-input" type="checkbox" name={t('Auto.label')}
											value={'AUTO'} id='configButtonAuto' checked={wordCount === 'AUTO'} toolTip={`Count detects automatically`}
											onChange={(e) => setWordCount('AUTO')} />
										<label class="form-check-label" for='configButtonAuto'>
											{t('Auto.label')}
										</label>
									</div>
								</li>
							</ul>
						</div>

					</div>
				</div>

				<div className={showFiles ? 'AiTextGen_AttachedFileCard' : 'AiTextGen_AttachedFileCard d-none'}>
					<div className='AiTextGen_AttachedFileCardHead'>
						<h5>{t('AttachedFile.label')}</h5>
						<span onClick={() => setShowFiles(false)}><BsXLg /></span>
					</div>
					<div className='AiTextGen_AttachedFileCardBody'>
						<p>{t('AttachFileForAI.label')}</p>
						<div className='AttachedFileList_Date'>
							<div className='AttachedFileDate'>
								{t('Today.label')}
							</div>
							<ul>
								{
									uploadedFiles.length == 0 ?
										<li>
											<span className='p-1'>{t('NoFilesUploaded.label')}</span>
										</li> :
										<>
											{
												uploadedFiles?.map((file, i) =>
													<>
														<li onClick={() => { setSelectedFile(file); setShowFiles(false) }}>
															{/* <span className='doc'><BsFileEarmarkText /></span> */}
															<span className='pdf'><BsFileEarmarkPdfFill /></span>
															<p>{file?.filename}</p>
															<span className='AIfileDelete'><BsTrash3 /></span>
														</li>
													</>
												)
											}
										</>
								}
							</ul>
						</div>
						{/* <div className='AttachedFileList_Date'>
							<div className='AttachedFileDate'>
								{t('Yesterday.label')}
							</div>
							<ul>
								{
									uploadedFiles.length == 0 ?
										<li>
											<span className='p-1'>{t('NoFilesUploaded.label')}</span>
										</li> :
										<>
											{
												uploadedFiles?.map((file, i) =>
													<>
														<li onClick={() => setSelectedFile(file)}>
															<span className='doc'><BsFileEarmarkText /></span>
															<p>{file?.filename}</p>
														</li>
													</>
												)
											}
										</>
								}
							</ul>
						</div>
						<div className='AttachedFileList_Date'>
							<div className='AttachedFileDate'>
								03 January 2025
							</div>
							<ul>
								{
									uploadedFiles.length == 0 ?
										<li>
											<span className='p-1'>{t('NoFilesUploaded.label')}</span>
										</li> :
										<>
											{
												uploadedFiles?.map((file, i) =>
													<>
														<li onClick={() => setSelectedFile(file)}>
															<span className='doc'><BsFileEarmarkText /></span> {file?.filename}
														</li>
													</>
												)
											}
										</>
								}
							</ul>
						</div>
						<div className='AttachedFileList_Date'>
							<div className='AttachedFileDate'>
								10 January 2025
							</div>
							<ul>
								{
									uploadedFiles.length == 0 ?
										<li>
											<span className='p-1'>{t('NoFilesUploaded.label')}</span>
										</li> :
										<>
											{
												uploadedFiles?.map((file, i) =>
													<>
														<li onClick={() => setSelectedFile(file)}>
															<span className='doc'><BsFileEarmarkText /></span>
															<p>{file?.filename}</p>
														</li>
													</>
												)
											}
										</>
								}
							</ul>
						</div>
						<div className='AttachedFileList_Date'>
							<div className='AttachedFileDate'>
								25 January 2025
							</div>
							<ul>
								{
									uploadedFiles.length == 0 ?
										<li>
											<span className='p-1'>{t('NoFilesUploaded.label')}</span>
										</li> :
										<>
											{
												uploadedFiles?.map((file, i) =>
													<>
														<li onClick={() => setSelectedFile(file)}>
															<span className='doc'><BsFileEarmarkText /></span> {file?.filename}
														</li>
													</>
												)
											}
										</>
								}
							</ul>
						</div> */}
					</div>
				</div>

				<div className={showHistory ? 'AiTextGen_HistoryCard' : 'AiTextGen_HistoryCard d-none'}>
					<div className='AiTextGen_HistoryCardHead'>
						<h5>{t('CopilotHistory.label')}</h5>
						<div className='history-header-actions'>
							{chatHistory.length > 0 && (
								<button 
									className='btn btn-sm btn-outline-danger me-2'
									onClick={clearChatHistory}
									title={t('ClearHistory.label')}
								>
									{t('ClearHistory.label')}
								</button>
							)}
							<span onClick={() => { setShowHistory(false); hideCopilotHistory(false) }}><BsXLg /></span>
						</div>
					</div>
					<div
						className='AiTextGen_HistoryCardBody'
						ref={historyScrollRef}
						style={{ maxHeight: '400px', overflowY: 'auto' }}
					>
						{/* Loading more indicator at top */}
						{isLoadingMore && (
							<div className='text-center p-2'>
								<div className="chatloader-inner ball-pulse">
									<div></div>
									<div></div>
									<div></div>
								</div>
								<small>{t('LoadingMoreHistory.label')}</small>
							</div>
						)}
						
						{historyLoading && chatHistory.length === 0 ? (
							<div className='text-center p-3'>
								<div className="chatloader-inner ball-pulse">
									<div></div>
									<div></div>
									<div></div>
								</div>
							</div>
						) : chatHistory.length === 0 ? (
							<div className='text-center p-3'>
								<p>{t('NoHistoryAvailable.label')}</p>
							</div>
						) : (
							Object.entries(groupHistoryByDate(chatHistory)).map(([dateKey, items]) => (
								<div key={dateKey} className='HistoryList_Date'>
									<div className='HistoryDate'>
										{dateKey}
									</div>
									<ul>
										{items.map((item, index) => {
											const itemId = `${dateKey}-${index}`;
											const isExpanded = expandedItems.has(itemId);
											const question = item.user_query || item.user_input || item.question || t('NoQuestionAvailable.label');
											const answer = item.response || item.answer || t('NoAnswerAvailable.label');
											const shouldTruncate = answer.length > 200;
											
											return (
												<li key={itemId} onClick={() => {
													// Add the selected history item to the chat window
													const historyItem = {
														question: question,
														answer: answer
													};
													setText(prevText => [...prevText, historyItem]);
													setShowHistory(false);
													hideCopilotHistory(false);
												}}>
												<div className='history-item'>
													<div className='history-question'>
														<p><strong>{question}</strong></p>
													</div>
													<div className='history-answer'>
														<p>
															{shouldTruncate && !isExpanded ? truncateText(answer) : answer}
														</p>
														{shouldTruncate && (
															<button 
																className='btn btn-link btn-sm p-0 text-primary'
																onClick={(e) => {
																	e.stopPropagation();
																	toggleReadMore(itemId);
																}}
															>
																{isExpanded ? t('ReadLess.label') : t('ReadMore.label')}
															</button>
														)}
													</div>
												</div>
											</li>
											);
										})}
									</ul>
								</div>
							))
						)}

						{historyLoading && chatHistory.length > 0 && (
							<div className='text-center p-2'>
								<div className="chatloader-inner ball-pulse">
									<div></div>
									<div></div>
									<div></div>
								</div>
							</div>
						)}

						{!hasMoreHistory && chatHistory.length > 0 && (
							<div className='text-center p-2'>
								<p className='text-muted small'>{t('NoMoreHistory.label')}</p>
							</div>
						)}
					</div>
				</div>

				{showClearHistoryConfirmation && (
					<ConfirmationModal
						modalShow={true}
						title={t('ClearHistory.label')}
						messageText={t('ClearHistoryConfirm.label')}
						confirmBtn={t('ClearHistory.label')}
						CancelBtn={t('Cancel.label')}
						callbackModal={handleClearHistoryConfirmation}
					/>
				)}

			</div>
		</div>
	);
}

export default withTranslation()(AITextGenWithPrompt);

